import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, Clock, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";
import Lenis from "lenis";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

// --- EmailJS Configuration ---
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const initialForm = { name: "", email: "", company: "", service: "", budget: "", message: "" };
const SERVICES = [
  "Film Making",
  "Video Editing",
  "Motion Graphics",
  "Web Development",
  "Branding",
  "Social Media",
  "Logo Designing",
  "Corporate AV Brochure",
  "Other"
];
const BUDGETS = ["< ₹1L", "₹1L – ₹5L", "₹5L – ₹20L", "₹20L+"];

const Growth = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4
    });

    let raf;
    const tick = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    window.lenis = lenis;
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }

    setSubmitting(true);

    const whatsappNumber = "919827555008";
    const message = `*Meraki Solutions - B2C/D2C Growth Call Request*%0A` +
      `---------------------------------------%0A` +
      `*Name:* ${form.name}%0A` +
      `*Email:* ${form.email}%0A` +
      `*Company:* ${form.company || "N/A"}%0A` +
      `*Service:* ${form.service || "N/A"}%0A` +
      `*Budget:* ${form.budget || "N/A"}%0A%0A` +
      `*Message:*%0A${form.message}`;

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

    try {
      const templateParams = {
        from_name: form.name,
        from_email: form.email,
        company: form.company || "N/A",
        service: form.service || "N/A",
        budget: form.budget || "N/A",
        message: form.message,
        to_name: "Meraki Solutions Team",
      };

      if (EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY) {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          templateParams,
          EMAILJS_PUBLIC_KEY
        );
      }
    } catch (error) {
      console.warn("EmailJS Backup failed, proceeding to WhatsApp:", error);
    }

    setSent(true);
    toast.success("Redirecting to WhatsApp for instant connection...");

    setTimeout(() => {
      window.open(whatsappUrl, "_blank");
      setForm(initialForm);
      setSent(false);
      setSubmitting(false);
      setIsModalOpen(false);
    }, 1500);
  };

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen selection:bg-[var(--gold)] selection:text-[var(--ink)] relative overflow-hidden">
      {/* Grain overlay */}
      <div className="grain" />

      <SEO
        title="Scale Your Brand | Meraki Solutions"
        description="Book your free growth call. We scale B2C and D2C brands to 10lac+ monthly revenue using the Meraki Method."
      />
      <Navbar />

      <main className="relative z-10 pt-32 pb-16 flex flex-col justify-center min-h-[calc(100vh-100px)]">
        <div className="container-xl px-6 py-16 text-center max-w-5xl mx-auto flex flex-col justify-center items-center gap-10">

          {/* Badge Tag */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="inline-flex items-center bg-[var(--ink-2)] px-6 py-2.5 rounded-full border border-[var(--rule)] shadow-2xl"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-[var(--gold)] mr-3 animate-pulse" />
            <span className="font-body font-semibold text-xs tracking-widest text-[var(--gold)] uppercase">
              FOR B2C & D2C BRANDS
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-display text-[32px] sm:text-5xl md:text-6xl lg:text-[76px] font-bold tracking-tight text-[var(--cream)] leading-[1.15] max-w-4xl balance"
          >
            We Scale{" "}
            <span className="gold-text italic font-serif-accent font-normal">B2C and D2C</span>
            {" "}Brands To{" "}
            <span className="relative inline-block gold-text italic font-serif-accent font-normal whitespace-nowrap">
              10lac+ Monthly
              {/* Hand-drawn organic SVG underline */}
              <svg
                className="absolute left-0 bottom-[-10px] w-full h-[12px] opacity-90"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M1,5 Q50,8 99,4 M5,7 Q50,9 95,6"
                  stroke="var(--gold)"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            {" "}Revenue
          </motion.h1>

          {/* Paragraph */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-2xl text-[var(--cream-muted)] text-base sm:text-lg md:text-xl font-body leading-relaxed mt-4"
          >
            Using the <span className="gold-text font-semibold">MERAKI Method</span>: A profit-first system that combines Branding, Performance Marketing, Content and Retention to drive consistent growth.
          </motion.p>

          {/* Action Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-6"
          >
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn-primary group inline-flex items-center gap-3 px-10 py-5 rounded-full"
              data-cursor="start"
            >
              Book Your Free Growth Call
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

        </div>
      </main>

      {/* Booking Form Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-md"
            onClick={() => {
              setIsModalOpen(false);
              setForm(initialForm);
              setSent(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative w-full max-w-2xl bg-[#14110f] border border-[rgba(201,169,97,0.25)] rounded-[32px] p-6 sm:p-10 md:p-12 shadow-2xl text-[var(--cream)] overflow-y-auto max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setForm(initialForm);
                  setSent(false);
                }}
                className="absolute top-6 right-6 p-2 rounded-full bg-white/5 text-[var(--cream-muted)] hover:text-white transition-colors"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>

              {!sent ? (
                <form onSubmit={onSubmit} className="space-y-6">
                  <div>
                    <span className="font-impact text-[10px] tracking-[0.3em] text-[var(--gold)] uppercase block mb-1">
                      GROWTH DISCOVERY
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl text-white">
                      Book your free growth call
                    </h3>
                    <p className="text-[13px] text-[var(--cream-muted)] mt-1">
                      We'll audit your brand, analyze your funnel, and sketch a scaling blueprint.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <label className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={onChange}
                        placeholder="John Doe"
                        className="w-full bg-transparent border-b border-[var(--rule)] py-2 text-[14px] text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors placeholder:text-[var(--muted)]"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={onChange}
                        placeholder="john@example.com"
                        className="w-full bg-transparent border-b border-[var(--rule)] py-2 text-[14px] text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors placeholder:text-[var(--muted)]"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <label className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={onChange}
                        placeholder="Acme Corp"
                        className="w-full bg-transparent border-b border-[var(--rule)] py-2 text-[14px] text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors placeholder:text-[var(--muted)]"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
                        Service Interested In
                      </label>
                      <select
                        name="service"
                        value={form.service}
                        onChange={onChange}
                        className="w-full bg-transparent border-b border-[var(--rule)] py-2 text-[14px] text-[var(--cream)] outline-none focus:border-[var(--gold)] appearance-none transition-colors"
                      >
                        <option value="" className="bg-[#14110f]">Select a service</option>
                        {SERVICES.map((s) => (
                          <option key={s} value={s} className="bg-[#14110f]">{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
                      Project Budget Range
                    </label>
                    <div className="flex flex-wrap gap-2.5">
                      {BUDGETS.map((b) => (
                        <button
                          type="button"
                          key={b}
                          onClick={() => setForm({ ...form, budget: b })}
                          className={`px-5 py-2 rounded-full border text-[11px] uppercase tracking-widest transition-all duration-300 ${
                            form.budget === b
                              ? "bg-[var(--gold)] border-[var(--gold)] text-[var(--ink)]"
                              : "border-[var(--rule)] text-[var(--muted)] hover:border-[var(--gold)] hover:text-[var(--gold)]"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={3}
                      value={form.message}
                      onChange={onChange}
                      placeholder="Tell us about your brand goals, channels, and monthly budget..."
                      className="w-full bg-transparent border-b border-[var(--rule)] py-2 text-[14px] text-[var(--cream)] outline-none focus:border-[var(--gold)] resize-none transition-colors placeholder:text-[var(--muted)]"
                    />
                  </div>

                  <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-[var(--rule)]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] flex items-center gap-2">
                      <Clock size={12} /> Response in &lt; 24 hours
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary min-w-[200px] w-full sm:w-auto"
                    >
                      {sent ? (
                        <span className="flex items-center gap-2">
                          <CheckCircle2 size={18} /> Directing...
                        </span>
                      ) : submitting ? (
                        <span>Sending...</span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Request Growth Audit <Send size={15} />
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/30 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="text-[var(--gold)]" size={32} />
                  </div>
                  <h3 className="font-display text-3xl text-white mb-4">Request Sent!</h3>
                  <p className="text-sm text-[var(--cream-muted)] leading-relaxed max-w-sm mx-auto mb-8">
                    Your scaling brief has been generated. Redirecting to WhatsApp for immediate booking with our growth team...
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Growth;
